import React, { useMemo } from "react";
import { withTranslation } from "react-i18next";
import {
  Chart as ChartJS,
  BarElement,
  CategoryScale,
  Legend,
  LinearScale,
  Tooltip,
} from "chart.js";
import { Bar } from "react-chartjs-2";
import { shouldShowTimelineExpectedBaseline } from "../../../../../core/constants/anomalyRuleCodes";
import {
  formatCompareDeviationLine,
  formatCompareTooltipLines,
  formatCompareValuesLine,
  formatTimelinePeriod,
} from "../utils/timelineFormatters";

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend);

const ActualVsExpectedChart = ({ t, dataPoints, ruleCode }) => {
  const showBaseline = shouldShowTimelineExpectedBaseline(ruleCode);

  const comparePoints = useMemo(() => {
    if (!showBaseline) return [];
    return dataPoints.filter(
      (dp) => dp.hasAnomaly && dp.expectedValue != null
    );
  }, [dataPoints, showBaseline]);

  const chartData = useMemo(
    () => ({
      labels: comparePoints.map((dp) => formatTimelinePeriod(dp.period)),
      datasets: [
        {
          label: t("MetricAnomalyTimeline.reportedValue.label"),
          data: comparePoints.map((dp) => dp.value),
          backgroundColor: "rgba(59, 130, 246, 0.75)",
          borderRadius: 4,
        },
        {
          label: t("AnomalyDetection.expectedValue.label"),
          data: comparePoints.map((dp) => dp.expectedValue),
          backgroundColor: "rgba(100, 116, 139, 0.55)",
          borderRadius: 4,
        },
      ],
    }),
    [comparePoints, t]
  );

  const options = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: "top",
          align: "end",
          labels: { font: { family: "Inter, sans-serif", size: 11 } },
        },
        tooltip: {
          callbacks: {
            afterTitle: (items) => {
              const idx = items[0]?.dataIndex;
              const dp = comparePoints[idx];
              if (!dp) return [];
              return formatCompareTooltipLines(dp, t);
            },
            label: (ctx) => {
              const label = ctx.dataset.label || "";
              const raw = ctx.raw;
              if (raw == null || Number.isNaN(raw)) {
                return `${label}: ${t("MetricAnomalyTimeline.notAvailable.label")}`;
              }
              return `${label}: ${raw.toLocaleString(undefined, { maximumFractionDigits: 4 })}`;
            },
          },
        },
      },
      scales: {
        x: { ticks: { font: { size: 10, family: "Inter, sans-serif" } } },
        y: { ticks: { font: { size: 11, family: "Inter, sans-serif" } } },
      },
    }),
    [comparePoints, t]
  );

  if (!showBaseline) {
    return (
      <div className="mat-compare-empty">
        {t("MetricAnomalyTimeline.compareExpectedNotApplicable.label")}
      </div>
    );
  }

  if (!comparePoints.length) {
    return (
      <div className="mat-compare-empty">
        {t("MetricAnomalyTimeline.noAnomaliesCompare.label")}
      </div>
    );
  }

  return (
    <div className="anomaly-chart-card">
      <div className="chart-card-header">
        <h6>{t("MetricAnomalyTimeline.compareChart.label")}</h6>
      </div>
      <div className="chart-card-body">
        <div className="mat-compare-narratives">
          {comparePoints.slice(0, 5).map((dp) => {
            const valuesLine = formatCompareValuesLine(dp, t);
            const deviationLine = formatCompareDeviationLine(dp, t);
            return (
              <div key={dp.period} className="mat-compare-line">
                <strong>{formatTimelinePeriod(dp.period)}</strong>
                <span className="mat-compare-values">{valuesLine}</span>
                {deviationLine && (
                  <span className="mat-compare-deviation">{deviationLine}</span>
                )}
              </div>
            );
          })}
        </div>
        <div className="mat-chart-height mat-chart-height-sm">
          <Bar data={chartData} options={options} />
        </div>
      </div>
    </div>
  );
};

export default withTranslation()(ActualVsExpectedChart);
